import {
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { nextClientSeq, clientNoFromSeq } from './projectStore';
import { editLeadIdentity, updateLead } from './leadStore';
import type { Lead } from './leads';
import {
  buildWaTriageDraft as buildWaTriageDraftPure,
  canonicalWaPhone,
  waTriageDraftId,
  waPhoneVariants,
  linkEmailToContactBody,
  linkWhatsAppToContactBody,
} from '../features/teaminbox/lib/attachExisting';

// Re-export the pure WA-triage helpers so the orchestrator imports them from
// the triage store (one import site) — the definitions live in the PURE
// attachExisting module so they unit-test without Firebase.
export { canonicalWaPhone, waTriageDraftId };

export type AiLabel =
  | 'probable_lead'
  | 'probable_vendor'
  | 'probable_partner'
  | 'probable_service_auto'
  | 'unknown_intent';

export type TriageAction = 'create_lead' | 'create_contact' | 'archive' | 'hold';

export type TriageChannel = 'whatsapp' | 'email';

export interface DraftContact {
  id: string;
  /** 'whatsapp' | 'email' — the lane the item arrived on (AD-015). */
  channel: string;
  status: string;
  firstMessage: string;
  aiLabel: AiLabel;
  aiConfidence: 'high' | 'medium' | 'low';
  autoArchived: boolean;
  autoArchivedReason?: string | null;
  triageAction?: TriageAction;
  triageActedBy?: string;
  triageActedAt?: unknown;
  messageCount?: number;
  lastMessageAt?: unknown;
  createdAt: unknown;

  /* ── WhatsApp identity (channel:'whatsapp') — absent on email docs ── */
  phone: string;
  canonicalPhone: string;
  waMessageId?: string;

  /* ── Email identity (channel:'email') — Shared Data Contract (Part 1 writes) ── */
  senderEmail?: string;
  senderName?: string | null;
  /**
   * MINIMAL-triage fallback identity (the `routeInboundEmail` path writes a
   * flat `email`/`name` pair, NOT the full `senderEmail`/`senderName`). The
   * mapper falls back to these so the read pane shows a sender, not blanks.
   */
  email?: string;
  name?: string;
  receivedAtAddress?: string;
  subject?: string;
  bodyText?: string;
  bodyHtml?: string | null;
  messageId?: string;
  knownContactId?: string | null;

  /* ── enrichment ── */
  /** Email triage assignment (Part 3 LIVE write — no email-ownership doc yet). */
  assignedTo?: string | null;
  assignedToName?: string | null;
  /**
   * Persisted read flag (Team Inbox email read-badge fix). Email has no
   * conversation/read doc, so opening an email writes `read:true` onto the
   * draft itself — this is what makes the unread "1" badge STAY cleared
   * across refreshes (session-only state reset on reload).
   */
  read?: boolean;
  readAt?: unknown;
  readBy?: string;
}

/** Listen to the active (non-archived, non-actioned) triage queue (all channels). */
export function listenTriageQueue(
  onData: (items: DraftContact[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'draft_contacts'),
    orderBy('lastMessageAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as DraftContact))
      .filter((item) => !item.autoArchived && !item.triageAction);
    onData(items);
  });
}

/** Listen to auto-archived items (legacy Messages screen). */
export function listenArchivedQueue(
  onData: (items: DraftContact[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'draft_contacts'),
    where('autoArchived', '==', true),
    orderBy('lastMessageAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DraftContact));
    onData(items);
  });
}

/**
 * Listen to the active EMAIL triage queue (channel:'email', not auto-archived,
 * not yet actioned). Part 1 writes these to `draft_contacts`; Part 3 reads them
 * to surface the public care@ inbox alongside WhatsApp. Errors keep the last
 * snapshot (best-effort, matches the WA poll behaviour).
 */
export function listenEmailQueue(
  onData: (items: DraftContact[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  // Filter status=='pending' SERVER-SIDE so an actioned/archived/held doc
  // never reaches the active queue (the old client-side `!triageAction` filter
  // let stale docs linger when the onSnapshot served cached data on an error).
  // `channel == && status ==` needs a composite index (firestore.indexes.json).
  // Sort client-side (no orderBy → no extra index field) for robustness.
  const q = query(
    collection(db, 'draft_contacts'),
    where('channel', '==', 'email'),
    where('status', '==', 'pending'),
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as DraftContact))
        // belt-and-braces: drop anything actioned/auto-archived even if a
        // legacy doc carries status:'pending' AND a triageAction (data drift).
        .filter((item) => !item.autoArchived && !item.triageAction)
        .sort((a, b) => emailSortMs(b) - emailSortMs(a));
      onData(items);
    },
    (err) => onError?.(err),
  );
}

/** PURE — best-effort epoch ms for sorting an email draft (Timestamp|number|null). */
export function emailSortMs(d: DraftContact): number {
  return tsToMs(d.lastMessageAt) || tsToMs(d.createdAt) || 0;
}

/** PURE — coerce a Firestore Timestamp / number / ISO string to epoch ms. */
export function tsToMs(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  const o = v as { toMillis?: () => number; seconds?: number };
  if (typeof o.toMillis === 'function') return o.toMillis();
  if (typeof o.seconds === 'number') return o.seconds * 1000;
  return 0;
}

/**
 * Listen to the HELD email triage items (channel:'email', triageAction:'hold').
 * Hold is a soft park, NOT a delete — but `listenEmailQueue` excludes any
 * actioned item, so a held email becomes invisible with no way back. This
 * listener powers the "Held" view + the un-hold recovery path. Index-free
 * (single equality on channel; held + sort filtered client-side).
 */
export function listenHeldEmails(
  onData: (items: DraftContact[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  // Held = status:'hold' (set by setTriageAction on a Hold). Query it
  // SERVER-SIDE so the held lane is exact and never collides with the active
  // queue. `channel == && status ==` reuses the same composite index family.
  const q = query(
    collection(db, 'draft_contacts'),
    where('channel', '==', 'email'),
    where('status', '==', 'hold'),
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as DraftContact))
        .filter((item) => !item.autoArchived)
        .sort((a, b) => emailSortMs(b) - emailSortMs(a));
      onData(items);
    },
    (err) => onError?.(err),
  );
}

/**
 * Persist the read flag for an email draft (read-badge fix). Merge write so
 * the badge clears and STAYS cleared across refreshes. Best-effort: a failed
 * write just leaves the session-scoped optimistic clear in place.
 */
export async function markEmailRead(
  draftId: string,
  byUid: string,
): Promise<void> {
  await setDoc(
    doc(db, 'draft_contacts', draftId),
    { read: true, readAt: serverTimestamp(), readBy: byUid },
    { merge: true },
  );
}

/**
 * Un-hold a held email draft — clears the triageAction so it returns to the
 * active email queue. Recovery path for an accidental Hold. status → 'pending'.
 */
export async function unholdDraftContact(
  draftId: string,
  byUid: string,
): Promise<void> {
  await setDoc(
    doc(db, 'draft_contacts', draftId),
    {
      triageAction: deleteField(),
      triageActedBy: deleteField(),
      triageActedAt: deleteField(),
      status: 'pending',
      unheldBy: byUid,
      unheldAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/** Set triage action on a draft_contact */
export async function setTriageAction(
  draftId: string,
  action: TriageAction,
  actedByUid: string,
): Promise<void> {
  await setDoc(
    doc(db, 'draft_contacts', draftId),
    {
      triageAction: action,
      triageActedBy: actedByUid,
      triageActedAt: serverTimestamp(),
      status: action === 'hold' ? 'hold' : 'actioned',
    },
    { merge: true },
  );
}

/**
 * Assign an EMAIL triage item to a teammate. There is no email-conversation
 * ownership doc yet (AD-014/015 cover routing, not ownership), so the
 * assignment is a real Firestore write onto the draft itself — surfaced as a
 * chip and a starting point for the future ownership model. LIVE.
 */
export async function assignDraftContact(
  draftId: string,
  toStaffId: string,
  toName: string,
  byUid: string,
): Promise<void> {
  await setDoc(
    doc(db, 'draft_contacts', draftId),
    {
      assignedTo: toStaffId,
      assignedToName: toName,
      assignedBy: byUid,
      assignedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/* ════════════════════ WhatsApp triage parity (Part 3) ════════════════════
 *
 * Email triage drives Create Lead / Create Contact / Archive / Hold off a
 * `DraftContact` doc (the care@ triage record). The WhatsApp router writes the
 * SAME holding doc for an unknown sender — `draft_contacts/{canonicalPhone}`
 * with `channel:'whatsapp_cloud'`, `status:'triage'` — keyed by the canonical
 * (digits-only, India-aware) phone, which is also the `triage:<id>`
 * conversation key. So WA reuses the EXACT same triage write path as Email:
 * we hydrate a DraftContact from the open WA conversation and hand it to
 * createLeadFromTriage / createContactFromTriage (or setTriageAction for
 * Archive/Hold). Two writes resolve the row with no duplicate/loop:
 *   1. the canonical draft_contacts doc (here — same as Email), then
 *   2. the server-only conv-index triage flag (waResolveTriage callable),
 *      wired in index.tsx so the row leaves the Triage filter.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Hydrate a DraftContact for a WhatsApp triage item (delegates to the PURE
 * builder in attachExisting; widened to the full DraftContact type the
 * createLead/createContact writers expect). The `id` is the canonical-phone
 * doc the WA router already wrote, so the writers' setTriageAction resolves
 * THAT canonical record (Email parity). `waTriageDraftId` is re-exported above.
 */
export function buildWaTriageDraft(o: {
  draftId: string;
  phone: string;
  firstMessage?: string;
}): DraftContact {
  return buildWaTriageDraftPure(o) as unknown as DraftContact;
}

/**
 * WhatsApp Create-Lead — PROMOTE the router's existing holding lead instead of
 * creating a duplicate. The WA router auto-creates a `leads` doc on first
 * inbound (`source:'whatsapp_inbound'`, `status:'triage'`, `autoCreated:true`)
 * — UNLIKE email, which has no auto-lead. So a WA Create-Lead must promote that
 * holding lead to `status:'new'` (the canonical enum) rather than addDoc a
 * second one. If (rare) no holding lead exists — e.g. a contentless first-touch
 * that never created one — it falls back to createLeadFromTriage (fresh create).
 * Either way the canonical draft_contacts record is resolved + the conv-index
 * triage flag is cleared by the orchestrator.
 *
 * Returns the promoted/created lead id. Matches on `phone` equality across the
 * reasonable WA variants (the router stores the raw `from`, e.g. `+9195…`).
 */
export async function promoteWaLeadFromTriage(
  draft: DraftContact,
  actedByUid: string,
): Promise<string> {
  const variants = waPhoneVariants(draft.phone || draft.canonicalPhone || '');
  if (variants.length > 0) {
    // Firestore `in` takes up to 10 values — the variant set is ≤6.
    const q = query(
      collection(db, 'leads'),
      where('source', '==', 'whatsapp_inbound'),
      where('phone', 'in', variants),
    );
    const snap = await getDocs(q);
    // Prefer the still-open holding lead (status:'triage'); else the newest.
    const open =
      snap.docs.find((d) => (d.data() as { status?: string }).status === 'triage') ||
      snap.docs[0];
    if (open) {
      // The `leads` update rule has a STRICT field allowlist (status / priority
      // / assigned_to / notes_internal / team_activity / updated_at /
      // updated_by) for a team session — so the promotion writes ONLY those:
      // flip status → 'new' (canonical enum) and append a team_activity note
      // recording the manual promotion + the draft id (audit lives in the note,
      // not in extra top-level fields that the rule would reject).
      await updateDoc(doc(db, 'leads', open.id), {
        status: 'new',
        team_activity: arrayUnion({
          at: new Date().toISOString(),
          by: actedByUid,
          what: `Promoted from WhatsApp triage (draft ${draft.id})`,
        }),
        updated_by: actedByUid,
        updated_at: serverTimestamp(),
      });
      await setTriageAction(draft.id, 'create_lead', actedByUid);
      return open.id;
    }
  }
  // No holding lead found → fall back to a fresh create (also resolves draft).
  return createLeadFromTriage(draft, actedByUid);
}

/** Create a lead from a triage item (channel-aware). */
export async function createLeadFromTriage(
  draft: DraftContact,
  actedByUid: string,
): Promise<string> {
  const isEmail = draft.channel === 'email';
  const ref = await addDoc(collection(db, 'leads'), {
    source: isEmail ? 'email_inbound' : 'whatsapp_inbound',
    status: 'new',
    channel: isEmail ? 'email' : 'whatsapp',
    phone: draft.phone || '',
    email: isEmail ? draft.senderEmail || '' : '',
    firstMessage: draft.firstMessage,
    subject: isEmail ? draft.subject || '' : '',
    autoCreated: false,
    fromTriage: true,
    draftContactId: draft.id,
    knownContactId: draft.knownContactId || null,
    created_via: 'portal_manual',
    // Prefer a human name (email From display) → email → phone placeholder.
    name: isEmail
      ? draft.senderName || draft.senderEmail || 'Email lead'
      : draft.phone || 'WhatsApp lead',
    createdAt: serverTimestamp(),
  });
  await setTriageAction(draft.id, 'create_lead', actedByUid);
  return ref.id;
}

/** Create a contact from a triage item (channel-aware). */
export async function createContactFromTriage(
  draft: DraftContact,
  name: string,
  contactType: string,
  actedByUid: string,
): Promise<string> {
  const isEmail = draft.channel === 'email';
  const seq = await nextClientSeq();
  const clientNo = clientNoFromSeq(seq);
  await setDoc(doc(db, 'clients', clientNo), {
    clientNo,
    name,
    contactType,
    phone: draft.phone || '',
    phones: draft.phone
      ? [{ number: draft.phone, label: 'primary', whatsApp: true }]
      : [],
    email: isEmail ? draft.senderEmail || '' : '',
    location: '',
    source: isEmail ? 'email_triage' : 'whatsapp_triage',
    createdVia: isEmail ? 'email-triage' : 'whatsapp-triage',
    createdBy: actedByUid,
    draftContactId: draft.id,
    createdAt: serverTimestamp(),
  });
  await setTriageAction(draft.id, 'create_contact', actedByUid);
  return clientNo;
}

/* ════════════════════ Create-from-FORM (item 2 — fixes the name=phone bug) ════════════════════
 *
 * The triage "Create Contact / Create Lead" used to default the contact NAME to
 * the phone number. The header-menu redesign opens a real FORM: Name (typed,
 * required), Phone + Email (pre-filled from the sender, editable). These writers
 * take the TYPED values so the contact/lead carries the human name + the
 * (possibly corrected) channel identity — never the number-as-name fallback. The
 * draft is still resolved (setTriageAction inside the writer) so no duplicate
 * lingers; the WA conv-index triage flag is cleared by the orchestrator.
 */

export interface TriageFormInput {
  /** Typed name (required — the writer assumes it is non-empty + trimmed). */
  name: string;
  /** Editable phone (pre-filled from the sender for WA; may be blank for email). */
  phone: string;
  /** Editable email (pre-filled for email triage; may be blank for WA). */
  email: string;
}

/**
 * Create a CONTACT from the typed form. Writes the typed name + the editable
 * phone/email identity (phones[] whatsApp:true when a phone is present so the
 * client-side WA resolver matches future inbounds; top-level email for the
 * server email router). Resolves the draft (create_contact). Returns C#####.
 */
export async function createContactFromForm(
  draft: DraftContact,
  form: TriageFormInput,
  contactType: string,
  actedByUid: string,
): Promise<string> {
  const isEmail = draft.channel === 'email';
  const phone = (form.phone || '').trim();
  const email =
    (form.email || '').trim() || (isEmail ? draft.senderEmail || draft.email || '' : '');
  const seq = await nextClientSeq();
  const clientNo = clientNoFromSeq(seq);
  await setDoc(doc(db, 'clients', clientNo), {
    clientNo,
    name: form.name.trim(),
    contactType,
    phone,
    phones: phone ? [{ number: phone, label: 'primary', whatsApp: true }] : [],
    email,
    emails: email ? [{ address: email, label: 'primary' }] : [],
    location: '',
    source: isEmail ? 'email_triage' : 'whatsapp_triage',
    createdVia: isEmail ? 'email-triage' : 'whatsapp-triage',
    createdBy: actedByUid,
    draftContactId: draft.id,
    createdAt: serverTimestamp(),
  });
  await setTriageAction(draft.id, 'create_contact', actedByUid);
  return clientNo;
}

/**
 * Create a LEAD from the typed form. Writes the typed name + editable identity
 * onto a fresh `leads/{autoId}` (satisfying the team-create rule: name/phone/
 * source-enum/created_via). Resolves the draft (create_lead). Returns the id.
 * (WA Create-Lead PROMOTES the router's holding lead via promoteWaLeadFromTriage
 * — this fresh-create form path is the EMAIL + the no-holding-lead fallback.)
 */
export async function createLeadFromForm(
  draft: DraftContact,
  form: TriageFormInput,
  actedByUid: string,
): Promise<string> {
  const isEmail = draft.channel === 'email';
  const phone = (form.phone || '').trim();
  const email =
    (form.email || '').trim() || (isEmail ? draft.senderEmail || draft.email || '' : '');
  const ref = await addDoc(collection(db, 'leads'), {
    source: isEmail ? 'email_inbound' : 'whatsapp_inbound',
    status: 'new',
    channel: isEmail ? 'email' : 'whatsapp',
    phone,
    email,
    firstMessage: draft.firstMessage || '',
    subject: isEmail ? draft.subject || '' : '',
    autoCreated: false,
    fromTriage: true,
    draftContactId: draft.id,
    knownContactId: draft.knownContactId || null,
    created_via: 'portal_manual',
    name: form.name.trim(),
    createdAt: serverTimestamp(),
  });
  await setTriageAction(draft.id, 'create_lead', actedByUid);
  return ref.id;
}

/* ════════════════════ "Add to existing" — link, don't duplicate ════════════════════
 *
 * When a triage item is from someone ALREADY a contact, "Add to existing"
 * attaches the message's channel identity to that contact instead of creating a
 * duplicate (Unified Comms Hub: one contact = one identity, channels = labels).
 * The merge patch is computed PURELY (attachExisting.ts) from the contact's
 * current doc, so an existing primary is never overwritten and a re-link is a
 * no-op. An append-only audit entry records who linked what, from where.
 * ───────────────────────────────────────────────────────────────────────────── */

/** PURE-ish read: the identity fields we merge against on a client doc. */
async function readClientIdentity(clientId: string): Promise<{
  email?: string;
  emails?: Array<{ address: string; label?: string }>;
  linked_google_emails?: string[];
  phone?: string;
  phones?: Array<{ number: string; label?: string; whatsApp?: boolean }>;
}> {
  const snap = await getDoc(doc(db, 'clients', clientId));
  if (!snap.exists()) {
    throw new Error('contact-not-found');
  }
  const d = snap.data() as Record<string, unknown>;
  return {
    email: typeof d.email === 'string' ? d.email : '',
    emails: Array.isArray(d.emails)
      ? (d.emails as Array<{ address: string; label?: string }>)
      : [],
    linked_google_emails: Array.isArray(d.linked_google_emails)
      ? (d.linked_google_emails as string[])
      : [],
    phone: typeof d.phone === 'string' ? d.phone : '',
    phones: Array.isArray(d.phones)
      ? (d.phones as Array<{ number: string; label?: string; whatsApp?: boolean }>)
      : [],
  };
}

/** Append-only audit of a link onto a contact (clients/{id}/links subcollection). */
async function writeLinkAudit(
  clientId: string,
  entry: Record<string, unknown>,
): Promise<void> {
  try {
    await addDoc(collection(db, 'clients', clientId, 'links'), {
      ...entry,
      at: serverTimestamp(),
    });
  } catch {
    // Audit is best-effort — the identity merge is the source of truth; a
    // failed audit write must not block the link.
  }
}

/**
 * Link an EMAIL triage draft to an EXISTING contact. Adds the sender email to
 * the contact (top-level `email` if blank + `linked_google_emails[]` + Person-
 * Hub `emails[]`), then resolves the draft (triageAction:'create_contact' with
 * the linked contact id) so it leaves the queue and no duplicate draft lingers.
 * Idempotent: a re-link writes only the audit row (empty merge patch).
 */
export async function linkEmailDraftToContact(
  draft: DraftContact,
  clientId: string,
  actedByUid: string,
): Promise<void> {
  const senderEmail = draft.senderEmail || draft.email || '';
  const existing = await readClientIdentity(clientId);
  const patch = linkEmailToContactBody(existing, senderEmail);
  if (Object.keys(patch).length > 0) {
    await updateDoc(doc(db, 'clients', clientId), {
      ...patch,
      updatedBy: actedByUid,
      updatedAt: serverTimestamp(),
    });
  }
  await writeLinkAudit(clientId, {
    channel: 'email',
    identity: senderEmail,
    fromDraftContactId: draft.id,
    by: actedByUid,
    fieldsChanged: Object.keys(patch),
  });
  // Resolve the draft so it leaves the queue. Record the linked contact id +
  // a 'link' marker so the audit shows it was attached, not freshly created.
  await setDoc(
    doc(db, 'draft_contacts', draft.id),
    {
      triageAction: 'create_contact',
      triageActedBy: actedByUid,
      triageActedAt: serverTimestamp(),
      status: 'actioned',
      linkedContactId: clientId,
      triageLinkedToExisting: true,
    },
    { merge: true },
  );
}

/**
 * Link a WHATSAPP conversation to an EXISTING contact. Adds the phone to the
 * contact (top-level `phone` if blank + `phones[]` with whatsApp:true) so both
 * the client-side resolver and the server router auto-match future inbounds.
 * There is no triage DRAFT for a WA conversation (wa senders thread directly),
 * so this only writes the contact identity + an audit row. Idempotent.
 */
export async function linkWhatsAppToContact(
  phone: string,
  clientId: string,
  actedByUid: string,
): Promise<void> {
  const existing = await readClientIdentity(clientId);
  const patch = linkWhatsAppToContactBody(existing, phone);
  if (Object.keys(patch).length > 0) {
    await updateDoc(doc(db, 'clients', clientId), {
      ...patch,
      updatedBy: actedByUid,
      updatedAt: serverTimestamp(),
    });
  }
  await writeLinkAudit(clientId, {
    channel: 'whatsapp',
    identity: phone,
    by: actedByUid,
    fieldsChanged: Object.keys(patch),
  });
}

/* ════════════════════ "Add to existing" → a LEAD (item 3) ════════════════════
 *
 * "Add to existing" now searches BOTH contacts and leads. Linking to a LEAD
 * writes the channel identity onto the lead's identity fields (name kept; phone/
 * email filled when blank) via the rules-safe pipeline-snapshot path (updateLead
 * whitelists notes_internal/status/team_activity/updated_*). A LEAD is a pre-
 * client; it has no clients/{id} doc, so the merge is just the top-level identity
 * on the pure Lead model (whatever was blank is filled; an existing value kept).
 */

/**
 * Link a channel identity (phone or email) onto an existing LEAD. Only a blank
 * identity field is filled (never overwrite). Persists via the rules-safe
 * `updateLead` snapshot path; an audit line rides in team_activity.
 */
export async function linkIdentityToLead(
  lead: Lead,
  channel: 'whatsapp' | 'email',
  identity: string,
  actedByUid: string,
): Promise<void> {
  const value = (identity || '').trim();
  if (!value) return;
  const patch =
    channel === 'email'
      ? lead.email && lead.email.trim()
        ? {}
        : { email: value }
      : lead.phone && lead.phone.trim()
        ? {}
        : { phone: value };
  const next = editLeadIdentity(lead, patch, new Date().toISOString());
  await updateLead(
    next,
    actedByUid,
    `Linked ${channel === 'email' ? 'email' : 'WhatsApp'} ${value} from Team Inbox`,
  );
}

/**
 * Link an EMAIL triage draft to an existing LEAD: attach the sender email +
 * resolve the draft (create_lead, linked) so it leaves the queue.
 */
export async function linkEmailDraftToLead(
  draft: DraftContact,
  lead: Lead,
  actedByUid: string,
): Promise<void> {
  const senderEmail = draft.senderEmail || draft.email || '';
  await linkIdentityToLead(lead, 'email', senderEmail, actedByUid);
  await setDoc(
    doc(db, 'draft_contacts', draft.id),
    {
      triageAction: 'create_lead',
      triageActedBy: actedByUid,
      triageActedAt: serverTimestamp(),
      status: 'actioned',
      linkedLeadId: lead.id,
      triageLinkedToExisting: true,
    },
    { merge: true },
  );
}
